import { createEffect, createSignal, on, onCleanup, Show, untrack } from 'solid-js';
import Modal from '../Modal';
import Text from '../Text';
import CurrencyInput from '../CurrencyInput';
import PlayerHeadAvatar from '../PlayerHeadAvatar';
import PlayerNameInput from '../PlayerNameInput';
import ScheduleEditor, { defaultSchedule, describeSchedule, parseSchedule, scheduleToJson, type ScheduleDraft } from '../ScheduleEditor';
import { DateRangePills } from '../DateRangePills';
import { useToast } from '../MessageBox';
import { useApi, isOk } from '../../api';
import { firstError } from '../../api/envelope';
import { useTranslation } from '../../hooks/useTranslation';
import { useDebounced } from '../../hooks/useDebounced';
import { formatDateTimeShort, fromDateTimeInput, toDateTimeInput } from '../../services/dateFormat';
import { formatCurrencyExact } from '../../services/numberFormat';
import type { ScheduledPaymentDto, ScheduledPaymentRequest } from '../../api';
import styles from './Payments.module.css';

type PaymentKind = 'Once' | 'Recurring';
type PaymentEnd = 'never' | 'count' | 'date';

const MESSAGE_LIMIT = 256;
const REASON_LIMIT = 50;
// GrieferGames takes a reason only from this amount up; PayPlugin.MinReasonAmount on the server.
const REASON_MIN_AMOUNT = 5_000;
const PLAYER_NAME = /^[A-Za-z0-9_]{3,16}$/;

interface Props {
    open: boolean;
    botId: string;
    editing: ScheduledPaymentDto | null;
    onClose: () => void;
    onSaved: (saved: ScheduledPaymentDto) => void;
}

interface Draft {
    playerName: string;
    amount: number | '';
    kind: PaymentKind;
    dueAt: string;
    schedule: ScheduleDraft;
    startsAt: string;
    end: PaymentEnd;
    maxPayments: number;
    endsAt: string;
    note: string;
    reason: string;
    message: string;
}

const paymentSchedule = (): ScheduleDraft => ({ ...defaultSchedule(), kind: 'weekly', days: [1], daysOfMonth: [1], minutesOfDay: [18 * 60], jitterMinutes: 0 });

function draftFrom(editing: ScheduledPaymentDto | null): Draft {
    const now = toDateTimeInput(new Date());
    if (!editing) {
        return {
            playerName: '', amount: '', kind: 'Once', dueAt: now, schedule: paymentSchedule(), startsAt: now,
            end: 'never', maxPayments: 12, endsAt: '', note: '', reason: '', message: '',
        };
    }
    return {
        playerName: editing.playerName,
        amount: editing.amount,
        kind: editing.kind === 'Recurring' ? 'Recurring' : 'Once',
        dueAt: toDateTimeInput(editing.dueAtUtc ?? new Date()),
        schedule: editing.scheduleJson ? parseSchedule(editing.scheduleJson) : paymentSchedule(),
        startsAt: toDateTimeInput(editing.startsAtUtc ?? new Date()),
        end: editing.maxPayments ? 'count' : editing.endsAtUtc ? 'date' : 'never',
        maxPayments: editing.maxPayments ?? 12,
        endsAt: editing.endsAtUtc ? toDateTimeInput(editing.endsAtUtc) : '',
        note: editing.note ?? '',
        reason: editing.reason ?? '',
        message: editing.message ?? '',
    };
}

function toRequest(draft: Draft): ScheduledPaymentRequest {
    const isoOrNull = (local: string) => fromDateTimeInput(local)?.toISOString() ?? null;
    const recurring = draft.kind === 'Recurring';
    return {
        playerName: draft.playerName.trim(),
        amount: draft.amount === '' ? 0 : draft.amount,
        note: draft.note.trim() || null,
        message: draft.message.trim() || null,
        reason: draft.amount !== '' && draft.amount >= REASON_MIN_AMOUNT ? draft.reason.trim() || null : null,
        kind: draft.kind,
        dueAtUtc: recurring ? null : isoOrNull(draft.dueAt),
        scheduleJson: recurring ? scheduleToJson({ ...draft.schedule, jitterMinutes: 0 }) : null,
        startsAtUtc: recurring ? isoOrNull(draft.startsAt) : null,
        endsAtUtc: recurring && draft.end === 'date' ? isoOrNull(draft.endsAt) : null,
        maxPayments: recurring && draft.end === 'count' ? draft.maxPayments : null,
    };
}

export default function ScheduledPaymentEditor(props: Props) {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [draft, setDraft] = createSignal<Draft>(draftFrom(null));
    const [saving, setSaving] = createSignal(false);
    const [occurrences, setOccurrences] = createSignal<string[]>([]);
    const [previewError, setPreviewError] = createSignal<string | null>(null);
    const debouncedDraft = useDebounced(draft, 300);
    const debouncedPlayer = useDebounced(() => draft().playerName.trim(), 400);
    let latestPreview = 0;

    createEffect(on(() => props.open, (open) => {
        if (!open) return;
        setDraft(draftFrom(props.editing));
        setOccurrences([]);
        setPreviewError(null);
    }));

    createEffect(() => {
        const current = debouncedDraft();
        if (!props.open) return;
        untrack(() => void loadPreview(current));
    });

    onCleanup(() => { latestPreview++; });

    const loadPreview = async (current: Draft) => {
        const requestId = ++latestPreview;
        const res = await api.payments.previewScheduled(props.botId, toRequest(current));
        if (requestId !== latestPreview) return;
        if (!isOk(res.data)) {
            setPreviewError(firstError(res.data) ?? t('PAYMENTS_TOAST_LOAD_FAILED'));
            return;
        }
        setOccurrences(res.data.value.occurrences ?? []);
        setPreviewError(res.data.value.error ?? null);
    };

    const patch = (next: Partial<Draft>) => setDraft({ ...draft(), ...next });

    const playerValid = () => PLAYER_NAME.test(draft().playerName.trim());
    const reasonAllowed = () => draft().amount !== '' && (draft().amount as number) >= REASON_MIN_AMOUNT;
    const amountValid = () => draft().amount !== '' && (draft().amount as number) > 0;
    const canSave = () => playerValid() && amountValid() && !previewError() && occurrences().length > 0 && !saving();

    const summary = () => {
        const current = draft();
        const who = current.playerName.trim() || t('PAYMENTS_EDITOR_THE_PLAYER');
        const amount = current.amount === '' ? '--' : formatCurrencyExact(current.amount);
        if (current.kind === 'Once') {
            const due = fromDateTimeInput(current.dueAt);
            return due && due.getTime() <= Date.now()
                ? t('PAYMENTS_EDITOR_SUMMARY_NOW', who, amount)
                : t('PAYMENTS_EDITOR_SUMMARY_ONCE', who, amount, formatDateTimeShort(due));
        }
        return t('PAYMENTS_EDITOR_SUMMARY_RECURRING', who, amount, describeSchedule(current.schedule, t));
    };

    const save = async () => {
        if (!canSave()) return;
        setSaving(true);
        const request = toRequest(draft());
        const res = props.editing
            ? await api.payments.updateScheduled(props.botId, props.editing.id, request)
            : await api.payments.createScheduled(props.botId, request);
        setSaving(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('PAYMENTS_TOAST_SAVE_FAILED'));
            return;
        }
        toast?.success(t(props.editing ? 'PAYMENTS_TOAST_UPDATED' : 'PAYMENTS_TOAST_CREATED'));
        props.onSaved(res.data.value);
    };

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            busy={saving()}
            maxWidth="42rem"
            title={<Text val={props.editing ? 'PAYMENTS_EDITOR_EDIT_TITLE' : 'PAYMENTS_EDITOR_NEW_TITLE'} />}
        >
            <form class="v" style={{ gap: 'var(--spacing-lg)' }} onSubmit={(e) => { e.preventDefault(); void save(); }}>
                <div class={styles.editorRow}>
                    <label class={`v ${styles.editorPlayer}`} style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_PLAYER" /></span>
                        <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                            <Show when={PLAYER_NAME.test(debouncedPlayer())} fallback={<span class={styles.avatarPlaceholder}><i class="fa-solid fa-user" /></span>}>
                                <PlayerHeadAvatar name={debouncedPlayer()} size={32} />
                            </Show>
                            <div style={{ flex: '1', 'min-width': '0' }}>
                                <PlayerNameInput
                                    value={draft().playerName}
                                    onInput={(playerName) => patch({ playerName })}
                                    placeholder={t('PAYMENTS_EDITOR_PLAYER_PLACEHOLDER')}
                                    ariaLabel={t('PAYMENTS_EDITOR_PLAYER')}
                                />
                            </div>
                        </div>
                    </label>
                    <label class={`v ${styles.editorAmount}`} style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_AMOUNT" /></span>
                        <CurrencyInput value={draft().amount} onChange={(amount) => patch({ amount })} placeholder="50.000" aria-label={t('PAYMENTS_EDITOR_AMOUNT')} />
                    </label>
                </div>

                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_KIND" /></span>
                    <DateRangePills<PaymentKind>
                        value={draft().kind}
                        onChange={(kind) => patch({ kind })}
                        pills={[
                            { value: 'Once', labelKey: 'PAYMENTS_KIND_ONCE' },
                            { value: 'Recurring', labelKey: 'PAYMENTS_KIND_RECURRING' },
                        ]}
                    />
                </div>

                <Show when={draft().kind === 'Once'}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_DUE" /></span>
                        <input type="datetime-local" value={draft().dueAt} onInput={(e) => patch({ dueAt: e.currentTarget.value })} class={styles.dateInput} />
                        <span class={styles.hint}><Text val="PAYMENTS_EDITOR_DUE_HINT" /></span>
                    </label>
                </Show>

                <Show when={draft().kind === 'Recurring'}>
                    <ScheduleEditor
                        value={draft().schedule}
                        onChange={(schedule) => patch({ schedule })}
                        upcomingRuns={occurrences()}
                        error={previewError()}
                        previewIncludesJitter
                        hideJitter
                        upcomingKey="PAYMENTS_SCHEDULE_UPCOMING"
                        clockHintKey="PAYMENTS_SCHEDULE_CLOCK_HINT"
                    />
                    <div class={styles.editorRow}>
                        <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1 1 12rem' }}>
                            <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_STARTS" /></span>
                            <input type="datetime-local" value={draft().startsAt} onInput={(e) => patch({ startsAt: e.currentTarget.value })} class={styles.dateInput} />
                        </label>
                        <div class="v" style={{ gap: 'var(--spacing-xs)', flex: '2 1 16rem' }}>
                            <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_ENDS" /></span>
                            <DateRangePills<PaymentEnd>
                                value={draft().end}
                                onChange={(end) => patch({ end })}
                                pills={[
                                    { value: 'never', labelKey: 'PAYMENTS_END_NEVER' },
                                    { value: 'count', labelKey: 'PAYMENTS_END_COUNT' },
                                    { value: 'date', labelKey: 'PAYMENTS_END_DATE' },
                                ]}
                            />
                            <Show when={draft().end === 'count'}>
                                <label class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <input
                                        type="number"
                                        min={1}
                                        class={styles.countInput}
                                        value={draft().maxPayments}
                                        onInput={(e) => patch({ maxPayments: Math.max(1, Math.round(Number(e.currentTarget.value) || 1)) })}
                                    />
                                    <span class={styles.hint}><Text val="PAYMENTS_END_COUNT_SUFFIX" /></span>
                                </label>
                            </Show>
                            <Show when={draft().end === 'date'}>
                                <input type="datetime-local" value={draft().endsAt} onInput={(e) => patch({ endsAt: e.currentTarget.value })} class={styles.dateInput} />
                            </Show>
                        </div>
                    </div>
                    <span class={styles.hint}><i class="fa-solid fa-coins" /> <Text val="PAYMENTS_EDITOR_CATCH_UP_HINT" /></span>
                </Show>

                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="PAYMENTS_NOTE" /></span>
                    <input type="text" maxLength={1000} placeholder={t('PAYMENTS_EDITOR_NOTE_PLACEHOLDER')} value={draft().note} onInput={(e) => patch({ note: e.currentTarget.value })} />
                </label>

                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabelRow}>
                        <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_REASON" /></span>
                        <span class={styles.counter}>{draft().reason.length}/{REASON_LIMIT}</span>
                    </span>
                    <input
                        type="text"
                        maxLength={REASON_LIMIT}
                        placeholder={t('PAYMENTS_EDITOR_REASON_PLACEHOLDER')}
                        value={draft().reason}
                        disabled={!reasonAllowed()}
                        onInput={(e) => patch({ reason: e.currentTarget.value })}
                    />
                    <span class={styles.hint}>
                        <Text val={reasonAllowed() ? 'PAYMENTS_EDITOR_REASON_HINT' : 'PAYMENTS_EDITOR_REASON_MIN'} />
                    </span>
                </label>

                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabelRow}>
                        <span class={styles.fieldLabel}><Text val="PAYMENTS_EDITOR_MESSAGE" /></span>
                        <span class={styles.counter}>{draft().message.length}/{MESSAGE_LIMIT}</span>
                    </span>
                    <input type="text" maxLength={MESSAGE_LIMIT} placeholder={t('PAYMENTS_EDITOR_MESSAGE_PLACEHOLDER')} value={draft().message} onInput={(e) => patch({ message: e.currentTarget.value })} />
                    <span class={styles.hint}><Text val="PAYMENTS_EDITOR_MESSAGE_HINT" /></span>
                </label>

                <div class={styles.summaryBox}>
                    <i class="fa-solid fa-circle-info" />
                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span>{summary()}</span>
                        <Show when={draft().kind === 'Once' && previewError()}>
                            {(error) => <span class={styles.summaryError}>{error()}</span>}
                        </Show>
                        <span class={styles.hint}><Text val="PAYMENTS_EDITOR_ONLINE_RULE" /></span>
                    </div>
                </div>

                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                    <button type="button" class="btn btn-secondary" onClick={props.onClose}><Text val="CANCEL" /></button>
                    <button type="submit" class="btn btn-primary" disabled={!canSave()}>
                        <Show when={saving()} fallback={<i class="fa-solid fa-check" />}><i class="fa-solid fa-spinner fa-spin" /></Show>{' '}
                        <Text val={props.editing ? 'PAYMENTS_EDITOR_SAVE' : 'PAYMENTS_EDITOR_CREATE'} />
                    </button>
                </div>
            </form>
        </Modal>
    );
}
